import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import BottomTabNav from '../shared/components/BottomTabNav';
import { formatCurrency, getUserCurrency, setUserCurrency as saveUserCurrency, getCurrencyByLocation, formatCurrencySync } from '../shared/utils/currency';
import useCurrencyCache from '../hooks/useCurrencyCache';
import { dataService } from '../services/dataService';
import { locationService } from '../services/locationService';
import { countryService } from '../services/countryService';
import { authService } from '../config/supabase';
import CountrySelectionModal from './CountrySelectionModal';
import './CustomerMenu.css';
import './CategoryGridOverride.css';
import '../styles/LoadingError.css';

const CustomerMenu = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeSection, setActiveSection] = useState('food'); // 'food' | 'grocery' | 'pharmacy' | 'accessories' | 'clothing' | 'electronics'
  const [showCartModal, setShowCartModal] = useState(false);
  const [lastAddedItem, setLastAddedItem] = useState(null);
  const [userInfo, setUserInfo] = useState(null); // Add user state
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [selectedShop, setSelectedShop] = useState(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Location and currency states
  const [userLocation, setUserLocation] = useState({ city: '', country: '' });
  const [userCurrency, setUserCurrency] = useState('PKR');
  const [locationFilter, setLocationFilter] = useState({ city: '', country: '' });
  
  // Data states
  const [foodItems, setFoodItems] = useState([]);
  const [groceryItems, setGroceryItems] = useState([]);
  const [pharmacyItems, setPharmacyItems] = useState([]);
  const [clothingItems, setClothingItems] = useState([]);
  const [accessoriesItems, setAccessoriesItems] = useState([]);
  const [electronicsItems, setElectronicsItems] = useState([]);
  const [foodCategories, setFoodCategories] = useState([]);
  const [groceryCategories, setGroceryCategories] = useState([]);
  const [pharmacyCategories, setPharmacyCategories] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [shops, setShops] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [clothingStores, setClothingStores] = useState([]);
  const [accessoriesStores, setAccessoriesStores] = useState([]);
  const [electronicsStores, setElectronicsStores] = useState([]);
  const [selectedClothingStore, setSelectedClothingStore] = useState(null);
  const [selectedAccessoriesStore, setSelectedAccessoriesStore] = useState(null);
  const [selectedElectronicsStore, setSelectedElectronicsStore] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  
  const { addToCart, getCartItemCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use currency cache hook
  const { formatCurrency: formatCurrencyCached, loading: currencyLoading } = useCurrencyCache();


  // Load user location and currency preferences
  const loadUserLocation = async () => {
    try {
      // First, get selected country and city from countryService (checks user profile and localStorage)
      const countryFromService = await countryService.getSelectedCountry();
      const cityFromService = await countryService.getSelectedCity();
      console.log('🌍 Loaded country from service:', countryFromService);
      console.log('🏙️ Loaded city from service:', cityFromService);
      
      // If no country is selected, show the modal
      if (!countryFromService) {
        setShowCountryModal(true);
        setUserLocation({ city: '', country: '' });
        setLocationFilter({ city: '', country: '' });
        setUserCurrency(getUserCurrency());
        return;
      }

      // Set the selected country immediately
      setSelectedCountry(countryFromService);
      console.log('✅ Set selectedCountry state to:', countryFromService);

      const currentUser = await authService.getCurrentUser();
      const email = currentUser?.email;
      const meta = currentUser?.user_metadata || {};

      // Use city from countryService first, then fallback to customer record or metadata
      let city = cityFromService || '';
      let country = countryFromService; // Use country from countryService
      let currency = '';

      // Prefer customers table; fallback to auth metadata
      if (email) {
        const customerRecord = await dataService.getCustomerByEmail(email).catch(() => null);
        if (customerRecord) {
          // Only use customer record city if cityService doesn't have one
          if (!city) city = customerRecord.city || '';
          // Use country from countryService, but allow override from customer record if it exists
          if (customerRecord.country) {
            country = customerRecord.country;
          }
          currency = customerRecord.currency || '';
        }
      }

      if (!city) city = meta.city || '';
      if (!currency) currency = meta.currency || '';

      // Apply state
      setUserLocation({ city, country });
      // Always set location filter from selected country and city
      setLocationFilter({ city, country });

      // Resolve currency precedence: explicit customer currency -> derived from country/city -> stored preference -> default
      const derived = await getCurrencyByLocation(country, city);
      const finalCurrency = currency || derived || getUserCurrency();
      setUserCurrency(finalCurrency);
      try { saveUserCurrency(finalCurrency); } catch (_) {}
    } catch (error) {
      console.error('Error loading user location:', error);
      // Check if country is selected, if not show modal
      const hasCountry = await countryService.hasSelectedCountry();
      if (!hasCountry) {
        setShowCountryModal(true);
      }
      setUserLocation({ city: '', country: '' });
      setLocationFilter({ city: '', country: '' });
      setUserCurrency(getUserCurrency());
    }
  };

  // Handle country selection from modal
  const handleCountrySelected = async (country) => {
    setSelectedCountry(country);
    setShowCountryModal(false);
    // Reload location to update filters
    await loadUserLocation();
  };

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load data with error handling for each method
        const foodItemsData = await dataService.getFoodItems().catch(err => {
          console.error('Error loading food items:', err);
          return [];
        });
        
        const groceryItemsData = await dataService.getGroceryItems().catch(err => {
          console.error('Error loading grocery items:', err);
          return [];
        });
        
        const foodCategoriesData = await dataService.getFoodCategories().catch(err => {
          console.error('Error loading food categories:', err);
          return [];
        });
        
        const groceryCategoriesData = await dataService.getGroceryCategories().catch(err => {
          console.error('Error loading grocery categories:', err);
          return [];
        });
        
        // Get country and city for filtering
        const countryForFilter = selectedCountry || userLocation.country || null;
        const cityForFilter = userLocation.city || null;
        
        const restaurantsData = await dataService.getRestaurants(countryForFilter, cityForFilter).catch(err => {
          console.error('Error loading restaurants:', err);
          return [];
        });
        
        const shopsData = await dataService.getShops(countryForFilter, cityForFilter).catch(err => {
          console.error('Error loading shops:', err);
          return [];
        });

        const pharmaciesData = await dataService.getPharmacies(countryForFilter, cityForFilter).catch(err => {
          console.error('Error loading pharmacies:', err);
          return [];
        });

        const pharmacyItemsData = await dataService.getPharmacyItems().catch(err => {
          console.error('Error loading pharmacy items:', err);
          return [];
        });

        // Load clothing, accessories, and electronics data
        // Note: Clothing items are stored in grocery_items table, filtered by category or shop_type
        const clothingStoresData = await dataService.getClothingStores(countryForFilter, cityForFilter).catch(err => {
          console.error('Error loading clothing stores:', err);
          return [];
        });

        // Load clothing items from clothing_items table only
        const clothingItemsData = await dataService.getClothingItems().catch(err => {
          console.error('Error loading clothing items:', err);
          return [];
        });

        const accessoriesStoresData = await dataService.getAccessoriesStores(countryForFilter, cityForFilter).catch(err => {
          console.error('Error loading accessories stores:', err);
          return [];
        });

        // Load accessories items from accessories_items table only
        const accessoriesItemsData = await dataService.getAccessoriesItems().catch(err => {
          console.error('Error loading accessories items:', err);
          return [];
        });

        const electronicsStoresData = await dataService.getElectronicsStores(countryForFilter, cityForFilter).catch(err => {
          console.error('Error loading electronics stores:', err);
          return [];
        });

        // Load electronics items from electronics_items table only
        const electronicsItemsData = await dataService.getElectronicsItems().catch(err => {
          console.error('Error loading electronics items:', err);
          return [];
        });

        console.log('Data loaded successfully:');
        console.log('Food items:', foodItemsData.length);
        console.log('Grocery items:', groceryItemsData.length);
        console.log('Food categories:', foodCategoriesData.length);
        console.log('Grocery categories:', groceryCategoriesData.length);
        console.log('Restaurants:', restaurantsData.length);
        console.log('Restaurants data:', restaurantsData);
        console.log('Shops:', shopsData.length);
        console.log('Clothing stores:', clothingStoresData.length);
        console.log('Clothing stores data:', clothingStoresData);
        console.log('Clothing items:', clothingItemsData.length);
        console.log('Clothing items data:', clothingItemsData);
        console.log('Accessories stores:', accessoriesStoresData.length);
        console.log('Accessories stores data:', accessoriesStoresData);
        console.log('Accessories items:', accessoriesItemsData.length);
        console.log('Accessories items data:', accessoriesItemsData);
        console.log('Electronics stores:', electronicsStoresData.length);
        console.log('Electronics stores data:', electronicsStoresData);
        console.log('Electronics items:', electronicsItemsData.length);
        console.log('Electronics items data:', electronicsItemsData);
        
        setFoodItems(foodItemsData);
        setGroceryItems(groceryItemsData);
        setFoodCategories(foodCategoriesData);
        setGroceryCategories(groceryCategoriesData);
        setRestaurants(restaurantsData);
        setShops(shopsData);
        setPharmacies(pharmaciesData);
        setPharmacyItems(pharmacyItemsData);
        setClothingStores(clothingStoresData);
        setClothingItems(clothingItemsData);
        setAccessoriesStores(accessoriesStoresData);
        setAccessoriesItems(accessoriesItemsData);
        setElectronicsStores(electronicsStoresData);
        setElectronicsItems(electronicsItemsData);

        // Load offers - fetch ALL active offers (filtering by section will happen in component)
        console.log('🔄 Loading offers from Supabase...');
        console.log('📍 Current section:', activeSection);
        console.log('📍 User location:', userLocation);
        
        // Load ALL offers (not filtered by section) - we'll filter by section in component
        const offersData = await dataService.getOffers(null, userLocation.country || null).catch(err => {
          console.error('❌ ERROR loading offers:', err);
          console.error('Error type:', err?.constructor?.name);
          console.error('Error message:', err?.message);
          console.error('Error code:', err?.code);
          console.error('Error details:', err?.details);
          console.error('Error hint:', err?.hint);
          console.error('Full error object:', err);
          
          // Check if it's an RLS error
          if (err?.code === '42501' || err?.message?.includes('row-level security') || err?.message?.includes('RLS')) {
            console.error('🚫 RLS POLICY ERROR: Row Level Security is blocking access to offers!');
            console.error('💡 SOLUTION: Run FIX_OFFERS_RLS_SIMPLE.sql in Supabase SQL Editor');
            console.error('📄 File location: FIX_OFFERS_RLS_SIMPLE.sql in project root');
            alert('⚠️ Offers cannot be loaded due to database permissions. Please contact support or check RLS policies.');
          }
          
          return [];
        });
        
        console.log('✅ Loaded offers from Supabase:', offersData?.length || 0, 'offers');
        if (offersData && offersData.length > 0) {
          console.log('📋 Offer details:', offersData.map(o => ({
            id: o.id,
            title: o.title,
            section: o.section,
            is_active: o.is_active,
            country_code: o.country_code,
            city: o.city,
            priority: o.priority,
            start_date: o.start_date,
            end_date: o.end_date
          })));
        } else {
          console.warn('⚠️ No offers found in database!');
          console.warn('🔍 Troubleshooting checklist:');
          console.warn('  1. Are offers saved in Supabase offers table?');
          console.warn('  2. Do offers have is_active = true?');
          console.warn('  3. Are RLS policies allowing SELECT on offers table?');
          console.warn('  4. Check browser console for detailed error messages');
          console.warn('  5. Run FIX_OFFERS_RLS_SIMPLE.sql if RLS is blocking');
        }
        setOffers(offersData || []);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    // Load user location first, then load data
    const initialize = async () => {
      await loadUserLocation();
      // Small delay to ensure location state is set
      setTimeout(() => {
        loadData();
      }, 100);
    };
    initialize();
  }, [selectedCountry]); // Reload when country changes

  // Reload offers when user location changes (not when section changes - we filter in component)
  useEffect(() => {
    const loadOffers = async () => {
      try {
        // Load ALL offers (not filtered by section) - filtering happens in component
        const offersData = await dataService.getOffers(null, userLocation.country || null).catch(err => {
          console.error('Error loading offers:', err);
          return [];
        });
        console.log('Reloaded offers on location change:', offersData?.length || 0, offersData);
        setOffers(offersData || []);
      } catch (err) {
        console.error('Error reloading offers:', err);
      }
    };
    // Only reload if location actually changed (not on section change)
    if (userLocation.country || userLocation.city) {
      loadOffers();
    }
  }, [userLocation.country, userLocation.city]);

  // Realtime for shops, grocery items, and new store types (so UI updates immediately when vendors save items)
  useEffect(() => {
    const unsubscribers = [];
    try {
      const shopsSub = dataService.subscribeToShops(async () => {
        const countryForFilter = selectedCountry || userLocation.country || null;
        const cityForFilter = userLocation.city || null;
        const shopsData = await dataService.getShops(countryForFilter, cityForFilter).catch(() => []);
        setShops(shopsData);
      });
      unsubscribers.push(() => shopsSub?.unsubscribe?.());

      const grocerySub = dataService.subscribeToGroceryItems(async () => {
        const items = await dataService.getGroceryItems().catch(() => []);
        setGroceryItems(items);
      });
      unsubscribers.push(() => grocerySub?.unsubscribe?.());

      // Real-time subscriptions for clothing stores and items
      const clothingStoresSub = dataService.subscribeToClothingStores(async () => {
        const countryForFilter = selectedCountry || userLocation.country || null;
        const cityForFilter = userLocation.city || null;
        const storesData = await dataService.getClothingStores(countryForFilter, cityForFilter).catch(() => []);
        setClothingStores(storesData);
      });
      unsubscribers.push(() => clothingStoresSub?.unsubscribe?.());

      const clothingItemsSub = dataService.subscribeToClothingItems(async () => {
        const items = await dataService.getClothingItems().catch(() => []);
        setClothingItems(items);
      });
      unsubscribers.push(() => clothingItemsSub?.unsubscribe?.());

      // Real-time subscriptions for accessories stores and items
      const accessoriesStoresSub = dataService.subscribeToAccessoriesStores(async () => {
        const countryForFilter = selectedCountry || userLocation.country || null;
        const cityForFilter = userLocation.city || null;
        const storesData = await dataService.getAccessoriesStores(countryForFilter, cityForFilter).catch(() => []);
        setAccessoriesStores(storesData);
      });
      unsubscribers.push(() => accessoriesStoresSub?.unsubscribe?.());

      const accessoriesItemsSub = dataService.subscribeToAccessoriesItems(async () => {
        const items = await dataService.getAccessoriesItems().catch(() => []);
        setAccessoriesItems(items);
      });
      unsubscribers.push(() => accessoriesItemsSub?.unsubscribe?.());

      // Real-time subscriptions for electronics stores and items
      const electronicsStoresSub = dataService.subscribeToElectronicsStores(async () => {
        const countryForFilter = selectedCountry || userLocation.country || null;
        const cityForFilter = userLocation.city || null;
        const storesData = await dataService.getElectronicsStores(countryForFilter, cityForFilter).catch(() => []);
        setElectronicsStores(storesData);
      });
      unsubscribers.push(() => electronicsStoresSub?.unsubscribe?.());

      const electronicsItemsSub = dataService.subscribeToElectronicsItems(async () => {
        const items = await dataService.getElectronicsItems().catch(() => []);
        setElectronicsItems(items);
      });
      unsubscribers.push(() => electronicsItemsSub?.unsubscribe?.());
    } catch (_) {}
    return () => {
      unsubscribers.forEach((fn) => { try { fn(); } catch (_) {} });
    };
  }, []);

  // Reset state when navigating to home page
  useEffect(() => {
    if (location.pathname === '/' || location.pathname === '/menu') {
      setSelectedRestaurant(null);
      setSelectedShop(null);
      setSelectedPharmacy(null);
      setSelectedClothingStore(null);
      setSelectedAccessoriesStore(null);
      setSelectedElectronicsStore(null);
      setSelectedCategory(null);
      setActiveCategory('All');
      setActiveSection('food');
      setSearchTerm('');
    }
  }, [location.pathname]);

  // Get current categories and items based on active section
  const getCurrentCategories = () => {
    if (activeSection === 'food') {
      // Return cuisine types from database food categories
      const cuisineTypes = ['All', ...foodCategories.map(cat => cat.name)];
      return cuisineTypes.map(cuisine => ({ id: cuisine, name: cuisine }));
    } else if (activeSection === 'grocery') {
      // If a shop is selected, show shop categories, otherwise show shop types
      if (selectedShop) {
        // Check if selectedShop is actually a clothing/accessories/electronics store
        if (selectedShop.type === 'clothing') {
          const uniqueCategories = ['All'];
          clothingItems
            .filter(item => item.store_id === selectedShop.id)
            .forEach(item => {
              if (item.category && !uniqueCategories.includes(item.category)) {
                uniqueCategories.push(item.category);
              }
            });
          return uniqueCategories.map(cat => ({ id: cat, name: cat }));
        } else if (selectedShop.type === 'accessories') {
          const uniqueCategories = ['All'];
          accessoriesItems
            .filter(item => item.store_id === selectedShop.id)
            .forEach(item => {
              if (item.category && !uniqueCategories.includes(item.category)) {
                uniqueCategories.push(item.category);
              }
            });
          return uniqueCategories.map(cat => ({ id: cat, name: cat }));
        } else if (selectedShop.type === 'electronics') {
          const uniqueCategories = ['All'];
          electronicsItems
            .filter(item => item.store_id === selectedShop.id)
            .forEach(item => {
              if (item.category && !uniqueCategories.includes(item.category)) {
                uniqueCategories.push(item.category);
              }
            });
          return uniqueCategories.map(cat => ({ id: cat, name: cat }));
        } else {
          return getShopCategories();
        }
      }
      // Return shop types dynamically from actual shop data
      // Filter out clothing, accessories, and electronics shops
      const uniqueShopTypes = ['All'];
      shops
        .filter(shop => {
          // Exclude clothing, accessories, and electronics shops
          if (!shop.shop_type) return true;
          const shopType = shop.shop_type.toLowerCase();
          return !shopType.includes('clothing') && 
                 !shopType.includes('fashion') && 
                 !shopType.includes('apparel') &&
                 !shopType.includes('accessories') &&
                 !shopType.includes('jewelry') &&
                 !shopType.includes('electronics') &&
                 !shopType.includes('mobile') &&
                 !shopType.includes('phone');
        })
        .forEach(shop => {
          if (shop.shop_type && !uniqueShopTypes.includes(shop.shop_type)) {
            uniqueShopTypes.push(shop.shop_type);
          }
        });
      console.log('Available shop types from database:', uniqueShopTypes);
      return uniqueShopTypes.map(type => ({ id: type, name: type }));
    } else if (activeSection === 'pharmacy') {
      // Pharmacy: if a pharmacy is selected, show derived categories; otherwise no top categories
      if (selectedPharmacy) {
        return getPharmacyCategories();
      }
      return [{ id: 'All', name: 'All' }];
    } else if (activeSection === 'clothing') {
      // Clothing: show categories from clothing_items table only
      const uniqueCategories = ['All'];
      const filteredStores = filterByLocation(clothingStores);
      const filteredStoreIds = new Set(filteredStores.map(store => store.id));
      
      // From clothing_items table only
      clothingItems
        .filter(item => filteredStoreIds.has(item.store_id))
        .forEach(item => {
          if (item.category && !uniqueCategories.includes(item.category)) {
            uniqueCategories.push(item.category);
          }
        });
      
      return uniqueCategories.map(cat => ({ id: cat, name: cat }));
    } else if (activeSection === 'accessories') {
      // Accessories: if a store is selected, show categories from that store; otherwise show all categories
      if (selectedAccessoriesStore) {
        const uniqueCategories = ['All'];
        accessoriesItems
          .filter(item => item.store_id === selectedAccessoriesStore.id)
          .forEach(item => {
            if (item.category && !uniqueCategories.includes(item.category)) {
              uniqueCategories.push(item.category);
            }
          });
        return uniqueCategories.map(cat => ({ id: cat, name: cat }));
      } else {
        // Show categories from all accessories items (filtered by location)
        const uniqueCategories = ['All'];
        const filteredStores = filterByLocation(accessoriesStores);
        const filteredStoreIds = new Set(filteredStores.map(store => store.id));
        
        accessoriesItems
          .filter(item => filteredStoreIds.has(item.store_id))
          .forEach(item => {
            if (item.category && !uniqueCategories.includes(item.category)) {
              uniqueCategories.push(item.category);
            }
          });
        
        return uniqueCategories.map(cat => ({ id: cat, name: cat }));
      }
    } else if (activeSection === 'electronics') {
      // Electronics: if a store is selected, show categories from that store; otherwise show all categories
      if (selectedElectronicsStore) {
        const uniqueCategories = ['All'];
        electronicsItems
          .filter(item => item.store_id === selectedElectronicsStore.id)
          .forEach(item => {
            if (item.category && !uniqueCategories.includes(item.category)) {
              uniqueCategories.push(item.category);
            }
          });
        return uniqueCategories.map(cat => ({ id: cat, name: cat }));
      } else {
        // Show categories from all electronics items (filtered by location)
        const uniqueCategories = ['All'];
        const filteredStores = filterByLocation(electronicsStores);
        const filteredStoreIds = new Set(filteredStores.map(store => store.id));
        
        electronicsItems
          .filter(item => filteredStoreIds.has(item.store_id))
          .forEach(item => {
            if (item.category && !uniqueCategories.includes(item.category)) {
              uniqueCategories.push(item.category);
            }
          });
        
        return uniqueCategories.map(cat => ({ id: cat, name: cat }));
      }
    } else {
      return [{ id: 'All', name: 'All' }];
    }
  };

  // Filter vendors by location - prefer exact city match, gracefully fall back to country/partial city
  const filterByLocation = (vendors) => {
    const filterCity = (locationFilter.city || '').toLowerCase().trim();
    const filterCountry = (locationFilter.country || '').toLowerCase().trim();

    // If no city and no country filter set, show all
    if (!filterCity && !filterCountry) {
      return vendors;
    }

    const normalize = (value) => (value || '').toLowerCase().replace(/\s+/g, ' ').trim();

    // First pass: exact city match
    let matched = vendors.filter((vendor) => {
      const vendorCity = normalize(vendor.city);
      return filterCity && vendorCity && vendorCity === normalize(filterCity);
    });

    // If no exact city match found, broaden criteria
    if (matched.length === 0) {
      matched = vendors.filter((vendor) => {
        const vendorCity = normalize(vendor.city);
        const vendorCountry = normalize(vendor.country);

        // 1) Country matches
        if (filterCountry && vendorCountry && vendorCountry === normalize(filterCountry)) {
          return true;
        }

        // 2) Partial city match (handles variants like "Kuwait" vs "Kuwait City")
        if (filterCity && vendorCity) {
          const c = normalize(filterCity);
          return vendorCity.includes(c) || c.includes(vendorCity);
        }

        return false;
      });
    }

    return matched;
  };

  const getCurrentItems = () => {
    console.log('getCurrentItems called:');
    console.log('activeSection:', activeSection);
    console.log('selectedRestaurant:', selectedRestaurant);
    console.log('selectedShop:', selectedShop);
    console.log('selectedClothingStore:', selectedClothingStore);
    console.log('selectedAccessoriesStore:', selectedAccessoriesStore);
    console.log('selectedElectronicsStore:', selectedElectronicsStore);
    console.log('locationFilter:', locationFilter);
    console.log('restaurants length:', restaurants.length);
    console.log('shops length:', shops.length);
    console.log('clothingStores length:', clothingStores.length);
    console.log('clothingItems length:', clothingItems.length);
    console.log('accessoriesStores length:', accessoriesStores.length);
    console.log('accessoriesItems length:', accessoriesItems.length);
    console.log('electronicsStores length:', electronicsStores.length);
    console.log('electronicsItems length:', electronicsItems.length);
    
    let items = [];
    
    if (activeSection === 'food') {
      if (selectedRestaurant) {
        // Filter food items by selected restaurant's vendor_id
        items = foodItems.filter(item => item.vendor_id === selectedRestaurant.id);
      } else {
        items = filterByLocation(restaurants); // Show restaurants filtered by location
      }
    } else if (activeSection === 'grocery') {
      if (selectedShop) {
        // Regular grocery shop only - filter out clothing, accessories, and electronics items
        const clothingCategories = ['men', 'women', 'kids', 'unisex', 'clothing', 'shoes', 'bags', 'apparel', 'fashion', 'clothes', 'garments'];
        const accessoriesCategories = ['accessories', 'jewelry', 'watches', 'bags', 'wallets', 'belts', 'sunglasses', 'home', 'home products', 'home decor'];
        const electronicsCategories = ['electronics', 'mobile', 'phone', 'laptop', 'computer', 'tablet', 'headphones', 'speakers', 'tv', 'camera'];
        
        items = groceryItems.filter(item => {
          // Exclude items from clothing/accessories/electronics stores
          if (selectedShop.shop_type) {
            const shopType = selectedShop.shop_type.toLowerCase();
            if (shopType.includes('clothing') || shopType.includes('fashion') || shopType.includes('apparel')) {
              return false;
            }
            if (shopType.includes('accessories') || shopType.includes('jewelry')) {
              return false;
            }
            if (shopType.includes('electronics') || shopType.includes('mobile') || shopType.includes('phone')) {
              return false;
            }
          }
          
          // Exclude items with clothing/accessories/electronics categories
          if (item.category) {
            const category = item.category.toLowerCase();
            if (clothingCategories.some(cat => category.includes(cat))) {
              return false;
            }
            if (accessoriesCategories.some(cat => category.includes(cat))) {
              return false;
            }
            if (electronicsCategories.some(cat => category.includes(cat))) {
              return false;
            }
          }
          
          // Only show items from the selected shop
          return item.shop_id === selectedShop.id;
        });
      } else {
        // Show only grocery shops, not clothing/accessories/electronics stores
        // Filter out shops that are clothing, accessories, or electronics stores
        const allShops = filterByLocation(shops);
        items = allShops.filter(shop => {
          if (!shop.shop_type) return true;
          const shopType = shop.shop_type.toLowerCase();
          // Exclude clothing, accessories, and electronics shops
          return !shopType.includes('clothing') && 
                 !shopType.includes('fashion') && 
                 !shopType.includes('apparel') &&
                 !shopType.includes('accessories') &&
                 !shopType.includes('jewelry') &&
                 !shopType.includes('electronics') &&
                 !shopType.includes('mobile') &&
                 !shopType.includes('phone');
        });
      }
    } else if (activeSection === 'pharmacy') {
      // Pharmacy
      if (selectedPharmacy) {
        items = pharmacyItems.filter(item => item.pharmacy_id === selectedPharmacy.id);
      } else {
        items = filterByLocation(pharmacies); // Show pharmacies filtered by location
      }
    } else if (activeSection === 'clothing') {
      if (selectedClothingStore) {
        // Show clothing items from selected store
        items = clothingItems.filter(item => item.store_id === selectedClothingStore.id);
      } else {
        // Show clothing stores filtered by location
        items = filterByLocation(clothingStores);
      }
    } else if (activeSection === 'accessories') {
      if (selectedAccessoriesStore) {
        // Show accessories items from selected store
        items = accessoriesItems.filter(item => item.store_id === selectedAccessoriesStore.id);
      } else {
        // Show accessories stores filtered by location
        items = filterByLocation(accessoriesStores);
      }
    } else if (activeSection === 'electronics') {
      if (selectedElectronicsStore) {
        // Show electronics items from selected store
        items = electronicsItems.filter(item => item.store_id === selectedElectronicsStore.id);
      } else {
        // Show electronics stores filtered by location
        items = filterByLocation(electronicsStores);
      }
    } else {
      items = [];
    }
    
    // Apply search filter
    if (searchTerm) {
      items = items.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.cuisine_type && item.cuisine_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.shop_type && item.shop_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.pharmacy_type && item.pharmacy_type.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    return items;
  };

  const getShopCategories = () => {
    // If a shop is selected, show categories from that shop's items; otherwise show all grocery categories
    if (selectedShop) {
      const uniqueCategories = ['All'];
      groceryItems
        .filter(item => item.shop_id === selectedShop.id)
        .forEach(item => {
          if (item.category && !uniqueCategories.includes(item.category)) {
            uniqueCategories.push(item.category);
          }
        });
      return uniqueCategories.map(cat => ({ id: cat, name: cat }));
    }
    // Since shops don't have categories field, return grocery categories
    return groceryCategories;
  };

  const getPharmacyCategories = () => {
    // Build categories dynamically from pharmacy items for selected pharmacy
    if (!selectedPharmacy) return pharmacyCategories;
    const cats = new Set(['All']);
    pharmacyItems.filter(i => i.pharmacy_id === selectedPharmacy.id).forEach(i => {
      if (i.category) cats.add(i.category);
    });
    return Array.from(cats).map(name => ({ id: name, name }));
  };

  const getItemsByCategory = () => {
    if (selectedShop) {
      // Filter grocery items by selected shop and group by category
      const shopItems = groceryItems.filter(item => item.shop_id === selectedShop.id);
      const grouped = {};
      
      shopItems.forEach(item => {
        if (!grouped[item.category]) {
          grouped[item.category] = [];
        }
        grouped[item.category].push(item);
      });
      
      return grouped;
    }
    return {};
  };

  const handleRestaurantSelect = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setActiveCategory('All');
  };

  const handleShopSelect = (shop) => {
    // Check if shop is actually a clothing/accessories/electronics store
    if (shop.type === 'clothing') {
      setSelectedClothingStore(shop);
      setActiveSection('clothing');
    } else if (shop.type === 'accessories') {
      setSelectedAccessoriesStore(shop);
      setActiveSection('accessories');
    } else if (shop.type === 'electronics') {
      setSelectedElectronicsStore(shop);
      setActiveSection('electronics');
    } else {
      setSelectedShop(shop);
    }
    setActiveCategory('All');
  };

  const handleClothingStoreSelect = (store) => {
    setSelectedClothingStore(store);
    setActiveCategory('All');
  };

  const handleAccessoriesStoreSelect = (store) => {
    setSelectedAccessoriesStore(store);
    setActiveCategory('All');
  };

  const handleElectronicsStoreSelect = (store) => {
    setSelectedElectronicsStore(store);
    setActiveCategory('All');
  };

  const handlePharmacySelect = (pharmacy) => {
    setSelectedPharmacy(pharmacy);
    setActiveCategory('All');
  };

  const handleBackToRestaurants = () => {
    setSelectedRestaurant(null);
    setActiveCategory('All');
  };

  const handleBackToShops = () => {
    setSelectedShop(null);
    setSelectedPharmacy(null);
    setSelectedClothingStore(null);
    setSelectedAccessoriesStore(null);
    setSelectedElectronicsStore(null);
    setSelectedCategory(null);
    setActiveCategory('All');
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setActiveCategory(category);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setActiveCategory('All');
  };

  const handleAddToCart = (item) => {
    // Add type field based on active section
      const itemWithType = {
        ...item,
        type: activeSection === 'food' ? 'food' : 
              (activeSection === 'grocery' ? 'grocery' : 
              (activeSection === 'pharmacy' ? 'pharmacy' : activeSection))
      };
    addToCart(itemWithType);
    setLastAddedItem(itemWithType);
    setShowCartModal(true);
  };

  const handleGoToCart = () => {
    setShowCartModal(false);
    navigate('/cart');
  };

  const handleContinueShopping = () => {
    setShowCartModal(false);
  };

  const currentItems = getCurrentItems();

  // Category-specific icon mapping
  const getCategoryIcon = (name) => {
    const key = (name || '').toString().trim().toLowerCase();
    const images = {
      'all': 'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=200&auto=format&fit=crop&q=60',
      // Food categories
      'appetizers': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=200&auto=format&fit=crop&q=60',
      'appetizer': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=200&auto=format&fit=crop&q=60',
      'burgers': 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=200&auto=format&fit=crop&q=60',
      'burger': 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=200&auto=format&fit=crop&q=60',
      'curry': 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=200&auto=format&fit=crop&q=60',
      'desserts': 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=200&auto=format&fit=crop&q=60',
      'dessert': 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=200&auto=format&fit=crop&q=60',
      'pasta': 'https://images.unsplash.com/photo-1621996346565-e3dbc646d9a9?w=200&auto=format&fit=crop&q=60',
      'pizza': 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=200&auto=format&fit=crop&q=60',
      'salads': 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=200&auto=format&fit=crop&q=60',
      'salad': 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=200&auto=format&fit=crop&q=60',
      'sushi': 'https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=200&auto=format&fit=crop&q=60',
      'tacos': 'https://images.unsplash.com/photo-1565299507177-b0ac66763828?w=200&auto=format&fit=crop&q=60',
      'taco': 'https://images.unsplash.com/photo-1565299507177-b0ac66763828?w=200&auto=format&fit=crop&q=60',
      // Cuisine types
      'italian': 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=200&auto=format&fit=crop&q=60',
      'american': 'https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=200&auto=format&fit=crop&q=60',
      'japanese': 'https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=200&auto=format&fit=crop&q=60',
      'mexican': 'https://images.unsplash.com/photo-1565299507177-b0ac66763828?w=200&auto=format&fit=crop&q=60',
      'indian': 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=200&auto=format&fit=crop&q=60',
      'chinese': 'https://images.unsplash.com/photo-1582878826629-29b7ad1cdc43?w=200&auto=format&fit=crop&q=60',
      'thai': 'https://images.unsplash.com/photo-1559314809-0f31657def5e?w=200&auto=format&fit=crop&q=60',
      'mediterranean': 'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=200&auto=format&fit=crop&q=60',
      'french': 'https://images.unsplash.com/photo-1551782450-a2132b4ba21d?w=200&auto=format&fit=crop&q=60',
      'korean': 'https://images.unsplash.com/photo-1582878826629-29b7ad1cdc43?w=200&auto=format&fit=crop&q=60',
      'vietnamese': 'https://images.unsplash.com/photo-1559314809-0f31657def5e?w=200&auto=format&fit=crop&q=60',
      'beverages': 'https://images.unsplash.com/photo-1511920170033-f8396924c348?w=200&auto=format&fit=crop&q=60',
      'beverage': 'https://images.unsplash.com/photo-1511920170033-f8396924c348?w=200&auto=format&fit=crop&q=60',
      'bakery': 'https://images.unsplash.com/photo-1541167760496-1628856ab772?w=200&auto=format&fit=crop&q=60',
      'snacks': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=200&auto=format&fit=crop&q=60',
      'snack': 'https://images.unsplash.com/photo-1544025162-d76694265947?w=200&auto=format&fit=crop&q=60',
      // Grocery categories
      'fruits': 'https://images.unsplash.com/photo-1547514701-42782101795e?w=200&auto=format&fit=crop&q=60',
      'fruit': 'https://images.unsplash.com/photo-1547514701-42782101795e?w=200&auto=format&fit=crop&q=60',
      'vegetables': 'https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=200&auto=format&fit=crop&q=60',
      'vegetable': 'https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=200&auto=format&fit=crop&q=60',
      'dairy': 'https://images.unsplash.com/photo-1550581190-9c1c48d21d6c?w=200&auto=format&fit=crop&q=60',
      // Shop types (grocery home screen) - refined images
      'grocery': 'https://images.unsplash.com/photo-1511690743698-d9d85f2fbf38?w=200&auto=format&fit=crop&q=60',
      'supermarket': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=200&auto=format&fit=crop&q=60',
      'electronics': 'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=200&auto=format&fit=crop&q=60',
      'pharmacy': 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=200&auto=format&fit=crop&q=60',
      'clothing': 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?w=200&auto=format&fit=crop&q=60',
      'home': 'https://images.unsplash.com/photo-1505691723518-36a1cccb9a62?w=200&auto=format&fit=crop&q=60'
    };
    return images[key];
  };
  console.log('Current items:', currentItems.length);
  console.log('Current items data:', currentItems);
  
  const filteredItems = currentItems.filter(item => {
    const matchesSearch = searchTerm === '' ||
      (item.name || '').toLowerCase().includes((searchTerm || '').toLowerCase()) ||
      (item.description || '').toLowerCase().includes((searchTerm || '').toLowerCase());

    // When an entity is selected (restaurant/shop/pharmacy/clothing/accessories/electronics), filter items by category if applicable
    if (selectedRestaurant || selectedShop || selectedPharmacy || selectedClothingStore || selectedAccessoriesStore || selectedElectronicsStore) {
      // For "All" category, show all items regardless of their category field (including items with null/undefined category)
      let matchesCategory = true;
      if (activeCategory !== 'All') {
        // When a specific category is selected, only show items that match that category
        // Handle both null/undefined category and string comparison
        matchesCategory = (item.category || '') === activeCategory;
      }
      // When "All" is selected, matchesCategory remains true, showing all items
      return matchesSearch && matchesCategory;
    }

    // List views (no selection): apply category filter per section
    let matchesCategory = true;
    if (activeSection === 'food') {
      matchesCategory = activeCategory === 'All' || item.cuisine_type === activeCategory;
    } else if (activeSection === 'grocery') {
      matchesCategory = activeCategory === 'All' || item.shop_type === activeCategory;
    } else if (activeSection === 'pharmacy') {
        // Pharmacy list has no top-level categories yet; always include
        matchesCategory = true;
      } else if (activeSection === 'clothing' || activeSection === 'accessories' || activeSection === 'electronics') {
        // For clothing/accessories/electronics, filter by category when no store is selected
        matchesCategory = activeCategory === 'All' || item.category === activeCategory;
      } else {
        matchesCategory = true;
      }

    return matchesSearch && matchesCategory;
  });

  // Show loading state
  if (loading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <div className="loading-text">Loading menu...</div>
        </div>
        <BottomTabNav />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="App">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <div className="error-text">{error}</div>
          <button 
            className="retry-btn" 
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
        <BottomTabNav />
      </div>
    );
  }

  return (
    <div className="App">
      {/* Company Header */}
      <div className="company-header">
        <div className="company-header-left">
          <div className="company-logo">
            <img src="https://akuoggticrcsctgkerfl.supabase.co/storage/v1/object/public/public-images/Screenshot_2025-11-20_005401__1_-removebg-preview.png" alt="Company logo" width="120" height="120" />
          </div>
          <div className="welcome-message">
            <h2 className="welcome-title">Welcome to ZepEat</h2>
            <p className="welcome-subtitle">Delicious food & fresh groceries delivered to your door</p>
          </div>
        </div>
        <div className="company-header-right">
          {userInfo ? (
            <div className="profile-avatar" onClick={() => navigate('/profile')}>
              <img src={userInfo.avatar} alt={userInfo.name} />
            </div>
          ) : (
            <div className="profile-icon" onClick={() => navigate('/profile')}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM3 21a9 9 0 0 1 18 0" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          )}
        </div>
      </div>
      
      <div className="menu-container">
        {/* Section Toggle */}
        <div className="section-toggle">
          <button 
            className={`section-btn ${activeSection === 'food' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('food');
              setActiveCategory('All');
              setSelectedRestaurant(null);
              setSelectedShop(null);
              setSelectedCategory(null);
            }}
          >
            Food
          </button>
          <button 
            className={`section-btn ${activeSection === 'grocery' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('grocery');
              setActiveCategory('All');
              setSelectedRestaurant(null);
              setSelectedShop(null);
              setSelectedCategory(null);
            }}
          >
            Grocery
          </button>
          <button 
            className={`section-btn ${activeSection === 'pharmacy' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('pharmacy');
              setActiveCategory('All');
              setSelectedRestaurant(null);
              setSelectedShop(null);
              setSelectedCategory(null);
            }}
          >
            Pharmacy
          </button>
          <button 
            className={`section-btn ${activeSection === 'accessories' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('accessories');
              setActiveCategory('All');
              setSelectedRestaurant(null);
              setSelectedShop(null);
              setSelectedPharmacy(null);
              setSelectedCategory(null);
            }}
          >
            Accessories
          </button>
          <button 
            className={`section-btn ${activeSection === 'clothing' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('clothing');
              setActiveCategory('All');
              setSelectedRestaurant(null);
              setSelectedShop(null);
              setSelectedPharmacy(null);
              setSelectedCategory(null);
            }}
          >
            Clothing
          </button>
          <button 
            className={`section-btn ${activeSection === 'electronics' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('electronics');
              setActiveCategory('All');
              setSelectedRestaurant(null);
              setSelectedShop(null);
              setSelectedPharmacy(null);
              setSelectedCategory(null);
            }}
          >
            Electronics
          </button>
        </div>

        {/* Location Filter */}
        <div className="location-filter" style={{
          margin: '15px 16px',
          padding: '12px',
          background: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '14px'
        }}>
          <span style={{ color: '#666', fontWeight: '500' }}>📍</span>
          <span style={{ color: '#374151', flex: 1 }}>
            Showing {activeSection === 'food' ? 'restaurants' : 
                     activeSection === 'grocery' ? 'grocery stores' : 
                     activeSection === 'pharmacy' ? 'pharmacies' :
                     activeSection === 'accessories' ? 'accessories' :
                     activeSection === 'clothing' ? 'clothing' :
                     activeSection === 'electronics' ? 'electronics' : 'items'} in:
          </span>
          <span style={{ 
            color: '#667eea', 
            fontWeight: '600',
            background: '#f0f4ff',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '12px'
          }}>
            {locationFilter.city && locationFilter.country 
              ? `${locationFilter.city}, ${locationFilter.country}`
              : 'Location not set'
            }
          </span>
          {locationFilter.city && (
            <button
              onClick={() => {
                // Reset location filter to show all (for testing only)
                setLocationFilter({ city: '', country: '' });
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px'
              }}
              title="Show all locations (Admin mode)"
            >
              ✕
            </button>
          )}
        </div>

        {/* Categories - horizontal - only show when no restaurant/shop/pharmacy/store selected */}
        {!selectedRestaurant && !selectedShop && !selectedPharmacy && !selectedClothingStore && !selectedAccessoriesStore && !selectedElectronicsStore && (
        <div className="categories-scroll" role="list">
          {getCurrentCategories().map((c) => (
            <button
              key={c.name}
              className={`category-item ${activeCategory === c.name ? 'active' : ''}`}
              onClick={() => setActiveCategory(c.name)}
            >
              <span className="category-avatar">
                <img 
                  src={c.icon || getCategoryIcon(c.name) || 'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=120&auto=format&fit=crop&q=60'} 
                  alt={c.name} 
                  className="category-icon" 
                  onError={(e) => { e.currentTarget.src = getCategoryIcon(c.name) || 'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=120&auto=format&fit=crop&q=60'; }}
                />
              </span>
              <span className="category-label">{c.name}</span>
            </button>
          ))}
        </div>
        )}

        {/* Offers banner - only show when no restaurant/shop selected */}
        {!selectedRestaurant && !selectedShop && !selectedPharmacy && !selectedClothingStore && !selectedAccessoriesStore && !selectedElectronicsStore && (() => {
          // Get relevant offers for current section
          console.log('=== OFFER FILTERING DEBUG ===');
          console.log('Total offers loaded from state:', offers.length);
          console.log('Active section:', activeSection);
          console.log('User location:', userLocation);
          
          if (offers.length > 0) {
            console.log('All offers in state:', offers.map(o => ({
              id: o.id,
              title: o.title,
              section: o.section,
              section_normalized: o.section ? String(o.section).toLowerCase().trim() : '',
              is_active: o.is_active,
              country_code: o.country_code,
              city: o.city,
              start_date: o.start_date,
              end_date: o.end_date,
              priority: o.priority
            })));
            console.log('Current activeSection:', activeSection, 'normalized:', activeSection ? String(activeSection).toLowerCase().trim() : '');
          } else {
            console.warn('⚠️ No offers in state! Check if offers are being loaded from database.');
          }
          
          // Filter offers by section - show offers for current section OR 'all' section
          const now = new Date();
          const relevantOffers = offers.filter(offer => {
            // Check if offer is active
            if (!offer.is_active) {
              return false;
            }
            
            // Check date range
            const startDate = offer.start_date ? new Date(offer.start_date) : null;
            const endDate = offer.end_date ? new Date(offer.end_date) : null;
            
            if (startDate && now < startDate) {
              return false; // Offer hasn't started yet
            }
            
            if (endDate && now > endDate) {
              return false; // Offer has ended
            }
            
            // Filter by section: show offers for current section OR 'all' section
            // Normalize section values (handle case sensitivity and whitespace)
            const offerSection = offer.section ? String(offer.section).toLowerCase().trim() : '';
            const currentSection = activeSection ? String(activeSection).toLowerCase().trim() : '';
            
            const sectionMatch = offerSection === 'all' || offerSection === currentSection;
            if (!sectionMatch) {
              console.log(`📂 Offer ${offer.id} (${offer.title}) filtered out - section mismatch: "${offerSection}" vs "${currentSection}" (original: "${offer.section}" vs "${activeSection}")`);
              return false; // Only show offers for current section or 'all'
            }
            
            console.log(`✅ Offer ${offer.id} (${offer.title}) matches section: ${offerSection} (original: ${offer.section})`);
            
            return true;
          }).sort((a, b) => {
            // Sort by: exact section match first (exact match > 'all'), then priority, then created_at
            const aExactMatch = a.section === activeSection;
            const bExactMatch = b.section === activeSection;
            
            // Exact section matches first
            if (aExactMatch && !bExactMatch) return -1;
            if (!aExactMatch && bExactMatch) return 1;
            
            // Then by priority (higher first)
            const priorityDiff = (b.priority || 0) - (a.priority || 0);
            if (priorityDiff !== 0) return priorityDiff;
            
            // Finally by created_at (newer first)
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
          });
          
          console.log('=== FILTERING RESULTS ===');
          console.log('Relevant offers after filtering:', relevantOffers.length);
          if (relevantOffers.length > 0) {
            console.log('✅ Offers that will be displayed:', relevantOffers.map(o => ({
              id: o.id,
              title: o.title,
              section: o.section,
              priority: o.priority
            })));
          } else {
            console.warn('⚠️ No offers match the filters for section:', activeSection);
            console.warn('Check: 1) Are offers created with section="' + activeSection + '" or section="all"? 2) Are offers active? 3) Are dates valid?');
          }
          
          // Get the highest priority offer
          const currentOffer = relevantOffers.length > 0 ? relevantOffers[0] : null;
          
          // Always show banner - either with real offer or fallback
          if (currentOffer) {
            console.log('✅ Displaying real offer from Supabase:', currentOffer.title);
            return (
              <div className="promo-banner" key={currentOffer.id} style={{ marginTop: '16px', marginBottom: '20px' }}>
                <div className="promo-text">
                  <div className="promo-title">{currentOffer.title || "Today's Offer"}</div>
                  <div className="promo-sub">
                    {currentOffer.subtitle || 'Special Offer'}
                  </div>
                  <div className="promo-foot">
                    {currentOffer.description || 
                     (currentOffer.minimum_order 
                       ? `on all orders above ${formatCurrencySync(currentOffer.minimum_order, userCurrency)}`
                       : 'Available now')}
                  </div>
                </div>
                <img 
                  className="promo-image" 
                  alt={currentOffer.title || 'Offer'}
                  src={currentOffer.image_url || currentOffer.image || 
                    (activeSection === 'food' 
                      ? 'https://images.unsplash.com/photo-1550547660-d9450f859349?w=300'
                     : activeSection === 'grocery'
                     ? 'https://images.unsplash.com/photo-1506806732259-39c2d0268443?w=300&auto=format&fit=crop&q=60'
                     : activeSection === 'pharmacy'
                     ? 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=300&auto=format&fit=crop&q=60'
                     : activeSection === 'clothing'
                     ? 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=300&auto=format&fit=crop&q=60'
                     : activeSection === 'accessories'
                     ? 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=300&auto=format&fit=crop&q=60'
                     : activeSection === 'electronics'
                     ? 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=300&auto=format&fit=crop&q=60'
                     : 'https://images.unsplash.com/photo-1550547660-d9450f859349?w=300'
                    )}
                  onError={(e) => {
                    // Fallback to default image if offer image fails to load
                    e.currentTarget.src = activeSection === 'food' 
                      ? 'https://images.unsplash.com/photo-1550547660-d9450f859349?w=300'
                     : activeSection === 'grocery'
                     ? 'https://images.unsplash.com/photo-1506806732259-39c2d0268443?w=300&auto=format&fit=crop&q=60'
                     : 'https://images.unsplash.com/photo-1550547660-d9450f859349?w=300';
                  }}
                />
              </div>
            );
          }
          
          // Show fallback banner if no real offers match
          console.log('📢 Showing default fallback promo (no matching offers available)');
          return (
            <div className="promo-banner" style={{ marginTop: '16px', marginBottom: '20px' }}>
              <div className="promo-text">
                <div className="promo-title">Today's Offer</div>
                <div className="promo-sub">
                   {activeSection === 'food' ? 'Free box of fries' : 
                    activeSection === 'grocery' ? 'Free delivery' :
                    activeSection === 'pharmacy' ? 'Free delivery' :
                    activeSection === 'clothing' ? 'Special offers' :
                    activeSection === 'accessories' ? 'Special offers' :
                    activeSection === 'electronics' ? 'Special offers' :
                    'Special offers'}
                </div>
                <div className="promo-foot">
                    {activeSection === 'food'
                      ? `on all orders above ${formatCurrencySync(15000, userCurrency)}`
                     : activeSection === 'grocery'
                     ? `on all grocery orders above ${formatCurrencySync(10000, userCurrency)}`
                     : activeSection === 'pharmacy'
                     ? `on all pharmacy orders above ${formatCurrencySync(10000, userCurrency)}`
                     : activeSection === 'clothing'
                     ? `on all clothing orders above ${formatCurrencySync(10000, userCurrency)}`
                     : activeSection === 'accessories'
                     ? `on all accessories orders above ${formatCurrencySync(10000, userCurrency)}`
                     : activeSection === 'electronics'
                     ? `on all electronics orders above ${formatCurrencySync(10000, userCurrency)}`
                     : `on all orders above ${formatCurrencySync(10000, userCurrency)}`}
                </div>
              </div>
              <img 
                className="promo-image" 
                 alt={activeSection === 'food' ? 'fries' : 
                      activeSection === 'grocery' ? 'grocery' :
                      activeSection === 'pharmacy' ? 'pharmacy' :
                      activeSection === 'clothing' ? 'clothing' :
                      activeSection === 'accessories' ? 'accessories' :
                      activeSection === 'electronics' ? 'electronics' : 'promo'}
                  src={activeSection === 'food' 
                    ? 'https://images.unsplash.com/photo-1550547660-d9450f859349?w=300'
                   : activeSection === 'grocery'
                   ? 'https://images.unsplash.com/photo-1506806732259-39c2d0268443?w=300&auto=format&fit=crop&q=60'
                   : activeSection === 'pharmacy'
                   ? 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=300&auto=format&fit=crop&q=60'
                   : activeSection === 'clothing'
                   ? 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=300&auto=format&fit=crop&q=60'
                   : activeSection === 'accessories'
                   ? 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=300&auto=format&fit=crop&q=60'
                   : activeSection === 'electronics'
                   ? 'https://images.unsplash.com/photo-1498049794561-7780e7231661?w=300&auto=format&fit=crop&q=60'
                   : 'https://images.unsplash.com/photo-1550547660-d9450f859349?w=300'
                  }
              />
            </div>
          );
        })()}

        {/* Search Bar - moved below offer banner */}
        <div className="search-bar">
          <div 
            className="search-icon" 
            style={{
              position: 'absolute',
              left: '18px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              fontSize: '18px',
              pointerEvents: 'none',
              zIndex: 999,
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            🔍
          </div>
          <input
            type="text"
            className="search-input"
            placeholder={`Search for ${activeSection}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              paddingLeft: '50px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Back button when restaurant/shop/pharmacy/clothing/accessories/electronics is selected */}
        {(selectedRestaurant || selectedShop || selectedPharmacy || selectedClothingStore || selectedAccessoriesStore || selectedElectronicsStore) && (
          <div className="back-button-container">
            <button className="back-button" onClick={
              selectedCategory ? handleBackToCategories : 
              selectedRestaurant ? handleBackToRestaurants : 
              (selectedShop || selectedPharmacy || selectedClothingStore || selectedAccessoriesStore || selectedElectronicsStore) ? handleBackToShops :
              handleBackToShops
            }>
                ◀ Back to {
                selectedCategory ? 'Categories' :
                 activeSection === 'food' ? 'Restaurants' : 
                 activeSection === 'grocery' ? 'Grocery' :
                 activeSection === 'pharmacy' ? 'Pharmacies' :
                 activeSection === 'accessories' ? 'Accessories' :
                 activeSection === 'clothing' ? 'Clothing' :
                 activeSection === 'electronics' ? 'Electronics' : 'Items'
              }
            </button>
          </div>
        )}

        {/* Restaurant/Shop/Pharmacy/Clothing/Accessories/Electronics Info when selected */}
        {(selectedRestaurant || selectedShop || selectedPharmacy || selectedClothingStore || selectedAccessoriesStore || selectedElectronicsStore) && (
          <div className="selected-entity-info">
            <div className="entity-header">
              <img 
                src={
                  selectedRestaurant?.image ||
                  selectedShop?.image ||
                  selectedShop?.image_url ||
                  selectedPharmacy?.image ||
                  selectedPharmacy?.image_url ||
                  selectedClothingStore?.image ||
                  selectedClothingStore?.image_url ||
                  selectedAccessoriesStore?.image ||
                  selectedAccessoriesStore?.image_url ||
                  selectedElectronicsStore?.image ||
                  selectedElectronicsStore?.image_url ||
                  'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=300'
                } 
                alt={selectedRestaurant?.name || selectedShop?.name || selectedPharmacy?.name || selectedClothingStore?.name || selectedAccessoriesStore?.name || selectedElectronicsStore?.name}
                className="entity-image"
                onError={(e)=>{ e.currentTarget.src='https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=300'; }}
              />
              <div className="entity-details">
                <h2 className="entity-name">{selectedRestaurant?.name || selectedShop?.name || selectedPharmacy?.name || selectedClothingStore?.name || selectedAccessoriesStore?.name || selectedElectronicsStore?.name}</h2>
                <p className="entity-description">{selectedRestaurant?.description || selectedShop?.description || selectedPharmacy?.description || selectedClothingStore?.description || selectedAccessoriesStore?.description || selectedElectronicsStore?.description}</p>
                <div className="entity-meta">
                  <span className="entity-rating">{selectedRestaurant?.rating || selectedShop?.rating || selectedPharmacy?.rating || selectedClothingStore?.rating || selectedAccessoriesStore?.rating || selectedElectronicsStore?.rating || 'New'}</span>
                  <span className="entity-delivery">{(selectedRestaurant?.delivery_time || selectedShop?.delivery_time || selectedPharmacy?.delivery_time || selectedClothingStore?.delivery_time || selectedAccessoriesStore?.delivery_time || selectedElectronicsStore?.delivery_time || 30)} min</span>
                </div>
              </div>
            </div>
        </div>
        )}

        {/* Section title */}
        <h2 className="section-title">
          {selectedRestaurant ? `${selectedRestaurant.name} Menu` : 
           selectedCategory ? `${selectedCategory} Items` :
           selectedShop ? `${selectedShop.name} Categories` :
           selectedPharmacy ? `${selectedPharmacy.name} Categories` :
           selectedClothingStore ? `${selectedClothingStore.name} Categories` :
           selectedAccessoriesStore ? `${selectedAccessoriesStore.name} Categories` :
           selectedElectronicsStore ? `${selectedElectronicsStore.name} Categories` :
           activeSection === 'food' ? 'Restaurants' : 
            activeSection === 'pharmacy' ? 'Pharmacies' : 
            activeSection === 'grocery' ? 'Grocery Stores' :
            activeSection === 'accessories' ? 'Accessories Stores' :
            activeSection === 'clothing' ? 'Clothing Stores' :
            activeSection === 'electronics' ? 'Electronics Stores' : 'Grocery'} 
          {searchTerm && `(${filteredItems.length} results)`}
        </h2>

        <div className="products-scroll">
          {filteredItems.length > 0 ? (
            // If showing restaurants/shops/pharmacies/clothing/accessories/electronics stores lists
            !selectedRestaurant && !selectedShop && !selectedPharmacy && !selectedClothingStore && !selectedAccessoriesStore && !selectedElectronicsStore ? (
              filteredItems.map((item) => (
                <div key={item.id} className="restaurant-shop-card" onClick={() => {
                  if (activeSection === 'food') {
                    handleRestaurantSelect(item);
                    } else if (activeSection === 'grocery') {
                      handleShopSelect(item);
                   } else if (activeSection === 'pharmacy') {
                      handlePharmacySelect(item);
                    } else if (activeSection === 'clothing') {
                      handleClothingStoreSelect(item);
                    } else if (activeSection === 'accessories') {
                      handleAccessoriesStoreSelect(item);
                    } else if (activeSection === 'electronics') {
                      handleElectronicsStoreSelect(item);
                    }
                }}>
                  <img src={item.image || item.image_url || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=300'} alt={item.name} className="restaurant-shop-thumb" onError={(e)=>{e.currentTarget.src='https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=300'}} />
                  <div className="restaurant-shop-info">
                    <div className="restaurant-shop-name">{item.name}</div>
                     <div className="restaurant-shop-cuisine">
                       {activeSection === 'food' ? item.cuisine_type : 
                        activeSection === 'grocery' ? item.shop_type : 
                        activeSection === 'pharmacy' ? item.pharmacy_type : 
                        activeSection === 'clothing' ? 'Clothing Store' :
                        activeSection === 'accessories' ? 'Accessories Store' :
                        activeSection === 'electronics' ? 'Electronics Store' :
                        activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}
                     </div>
                    <div className="restaurant-shop-description">{item.description}</div>
                    <div className="restaurant-shop-meta">
                      <span className="rating">{item.rating ? Number(item.rating).toFixed(1) : 'New'}</span>
                      <span className="delivery-time">{(item.delivery_time ?? 30)} min</span>
                      <span className="delivery-fee">
                        {formatCurrencyCached(
                          Number(item.delivery_fee ?? 299), 
                          // For clothing, accessories, and electronics, use location-based currency
                          (activeSection === 'clothing' || activeSection === 'accessories' || activeSection === 'electronics')
                            ? userCurrency
                            : (item.currency || userCurrency)
                        )} delivery
                      </span>
                      {/* Debug: Check currency values */}
                      {(() => {
                        console.log('Debug Currency:', {
                          itemName: item.name,
                          itemCurrency: item.currency,
                          userCurrency: userCurrency,
                          finalCurrency: item.currency || userCurrency,
                          deliveryFee: item.delivery_fee,
                          currencyLoading: currencyLoading
                        });
                        return null;
                      })()}
                    </div>
                    <div className="restaurant-shop-status">
                      <span className="online-status">🟢 Online</span>
                      <span className="min-order">Min: {formatCurrencyCached(Number(item.minimum_order ?? 1000), item.currency || userCurrency)}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              // If showing categories from selected shop/pharmacy/clothing/accessories/electronics
              ((selectedShop && !selectedCategory) || (selectedPharmacy && !selectedCategory) || (selectedClothingStore && !selectedCategory) || (selectedAccessoriesStore && !selectedCategory) || (selectedElectronicsStore && !selectedCategory)) ? (
                <div className="categories-grid">
                  {(activeSection === 'grocery' ? getShopCategories() : 
                    activeSection === 'pharmacy' ? getPharmacyCategories() :
                    activeSection === 'clothing' ? getCurrentCategories() :
                    activeSection === 'accessories' ? getCurrentCategories() :
                    activeSection === 'electronics' ? getCurrentCategories() :
                    getShopCategories()).map((category) => (
                    <div key={category.id} className="category-card" onClick={() => handleCategorySelect(category.name)}>
                      <img 
                        src={category.icon || getCategoryIcon(category.name) || 'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=200&auto=format&fit=crop&q=60'} 
                        alt={category.name} 
                        className="category-card-image" 
                        onError={(e) => { e.currentTarget.src = getCategoryIcon(category.name) || 'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=200&auto=format&fit=crop&q=60'; }}
                      />
                      <div className="category-card-name">{category.name}</div>
                    </div>
                  ))}
                </div>
              ) : (
                // If showing items from selected category
                selectedCategory ? (
                  <div className="category-items">
                    {(() => {
                      // Get items based on active section and selected store
                      let categoryItems = [];
                      if (activeSection === 'food' && selectedRestaurant) {
                        categoryItems = foodItems.filter(item => item.vendor_id === selectedRestaurant.id && item.category === selectedCategory);
                      } else if (activeSection === 'grocery' && selectedShop) {
                        categoryItems = groceryItems.filter(item => item.shop_id === selectedShop.id && item.category === selectedCategory);
                      } else if (activeSection === 'pharmacy' && selectedPharmacy) {
                        categoryItems = pharmacyItems.filter(item => item.pharmacy_id === selectedPharmacy.id && item.category === selectedCategory);
                      } else if (activeSection === 'clothing' && selectedClothingStore) {
                        categoryItems = clothingItems.filter(item => item.store_id === selectedClothingStore.id && item.category === selectedCategory);
                      } else if (activeSection === 'accessories' && selectedAccessoriesStore) {
                        categoryItems = accessoriesItems.filter(item => item.store_id === selectedAccessoriesStore.id && item.category === selectedCategory);
                      } else if (activeSection === 'accessories' && !selectedAccessoriesStore) {
                        // If no store selected but category is selected, show items from all stores in that category
                        const filteredStores = filterByLocation(accessoriesStores);
                        const filteredStoreIds = new Set(filteredStores.map(store => store.id));
                        categoryItems = accessoriesItems.filter(item => 
                          filteredStoreIds.has(item.store_id) && item.category === selectedCategory
                        );
                      } else if (activeSection === 'electronics' && selectedElectronicsStore) {
                        categoryItems = electronicsItems.filter(item => item.store_id === selectedElectronicsStore.id && item.category === selectedCategory);
                      } else if (activeSection === 'electronics' && !selectedElectronicsStore) {
                        // If no store selected but category is selected, show items from all stores in that category
                        const filteredStores = filterByLocation(electronicsStores);
                        const filteredStoreIds = new Set(filteredStores.map(store => store.id));
                        categoryItems = electronicsItems.filter(item => 
                          filteredStoreIds.has(item.store_id) && item.category === selectedCategory
                        );
                      } else if (activeSection === 'clothing' && !selectedClothingStore) {
                        // If no store selected but category is selected, show items from all stores in that category
                        const filteredStores = filterByLocation(clothingStores);
                        const filteredStoreIds = new Set(filteredStores.map(store => store.id));
                        categoryItems = clothingItems.filter(item => 
                          filteredStoreIds.has(item.store_id) && item.category === selectedCategory
                        );
                      }
                      
                      return categoryItems.map((item) => {
                        const itemType = activeSection === 'food' ? 'food' : 
                                        activeSection === 'grocery' ? 'grocery' : 
                                        activeSection === 'pharmacy' ? 'pharmacy' : 
                                        activeSection;
                        return (
                          <Link key={`${itemType}-${item.id}`} to={`/item/${itemType}/${item.id}`} className="product-link">
                            <div className="product-card">
                              <img src={item.image || item.image_url || 'https://via.placeholder.com/150'} alt={item.name} className="product-thumb" onError={(e) => { e.target.src = 'https://via.placeholder.com/150'; }} />
                              <div className="product-name">{item.name}</div>
                              <div className="product-meta">
                                <span className="price">{formatCurrencyCached(item.price, item.vendor_currency || item.currency || userCurrency)}</span>
                                <button className="circle-add" onClick={(e) => { e.preventDefault(); handleAddToCart(item); }}>+</button>
                              </div>
                            </div>
                          </Link>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  // If showing items from selected restaurant OR clothing/accessories/electronics products directly
            filteredItems.map((item) => {
              // Determine item type for routing
              let itemType = 'food';
              if (activeSection === 'grocery') {
                itemType = 'grocery';
              } else if (activeSection === 'pharmacy') {
                itemType = 'pharmacy';
              } else if (activeSection === 'clothing') {
                // Check if item is from grocery_items (has shop_id) or clothing_items (has store_id)
                itemType = item.shop_id ? 'grocery' : 'clothing';
              } else if (activeSection === 'accessories') {
                itemType = item.shop_id ? 'grocery' : 'accessories';
              } else if (activeSection === 'electronics') {
                itemType = item.shop_id ? 'grocery' : 'electronics';
              }
              
              return (
                <Link key={`${activeSection}-${item.id}`} to={`/item/${itemType}/${item.id}`} className="product-link">
                  <div className="product-card">
                    <img src={item.image || item.image_url || 'https://via.placeholder.com/150'} alt={item.name} className="product-thumb" onError={(e) => { e.target.src = 'https://via.placeholder.com/150'; }} />
                    <div className="product-name">{item.name}</div>
                    <div className="product-meta">
                      <span className="price">
                        {formatCurrencyCached(
                          item.price, 
                          // For clothing, accessories, and electronics, use location-based currency
                          (activeSection === 'clothing' || activeSection === 'accessories' || activeSection === 'electronics')
                            ? userCurrency
                            : (item.vendor_currency || item.currency || userCurrency)
                        )}
                      </span>
                      <button className="circle-add" onClick={(e) => { e.preventDefault(); handleAddToCart(item); }}>+</button>
                    </div>
                  </div>
                </Link>
              );
            })
                )
              )
            )
          ) : (
            <div className="no-results">
              <div className="no-results-icon">🔍</div>
              <div className="no-results-text">No {activeSection} items found</div>
              <div className="no-results-sub">Try adjusting your search or browse categories</div>
              </div>
          )}
        </div>
      </div>

      {/* Cart Success Modal */}
      {showCartModal && lastAddedItem && (
        <div className="cart-success-modal-overlay" onClick={handleContinueShopping}>
          <div className="cart-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cart-success-icon">✅</div>
            <h3>Item Added to Cart!</h3>
            <p>You've added <strong>{lastAddedItem.name}</strong> to your cart.</p>
            <div className="cart-success-actions">
              <button className="cart-success-action-btn" onClick={handleGoToCart}>Go to Cart</button>
              <button className="cart-success-action-btn" onClick={handleContinueShopping}>Continue Shopping</button>
            </div>
          </div>
        </div>
      )}

      {/* Country Selection Modal */}
      {showCountryModal && (
        <CountrySelectionModal onCountrySelected={handleCountrySelected} />
      )}
      
      <BottomTabNav />
    </div>
  );
};

export default CustomerMenu;
